/**
 * StaffAbsencesTab — the dates one person cannot be booked.
 *
 * Data: partial — ops: listAvailabilityOverrides (GET /v1/admin/overrides),
 * createAvailabilityOverride (POST /v1/admin/overrides),
 * deleteAvailabilityOverride (DELETE /v1/admin/overrides/{overrideId}).
 * The ops are proven live by CC through the admin API (0060), and the design
 * preview answers them from the serving engine (0928h). This screen itself has
 * not been observed on the wire yet. A failed read shows its error; nothing is
 * ever shown as saved before the awaited call resolves.
 *
 * WHAT AN ABSENCE DOES (CC 0060, live 28 Sept): a closed staff override removes
 * the person from booking-page slots and from staff assignment. The weekly
 * hours on the Availability tab are NOT part of slot calculation (operator
 * decision): store bookings follow the store's opening hours.
 *
 * WHY AN AVAILABILITY OVERRIDE, AND NOT `staff.timeOff`. An absence has to stop
 * bookings, and only one record does that: `availability_overrides`
 * (`override_kind: 'closed'`, `subject_kind: 'staff'`), which the availability
 * worker subtracts from the weekly pattern. `staff.timeOff` on the roster record
 * is stored verbatim and read by nothing (see the note on StaffTimeOffRange in
 * api-contracts staff.ts), so an absence written there would look saved and
 * still be bookable. It is the same row the member's own "Block time" on My Day
 * writes (`/v1/me/overrides`), so those blocks show here too — this tab is every
 * closure on the person's time, from either side.
 *
 * Whole days are stated in the PERSON's zone (the one the Availability tab
 * uses): an absence on 3 October starts at their midnight, not the viewer's.
 * With no valid zone recorded, the viewer's zone is used for read-only display, while
 * add/save stays disabled until the staff zone is repaired. Partial-day wall clocks
 * must resolve to exactly one instant; DST gaps and repeats are rejected.
 *
 * Capability: the admin lane is `settings.read` / `settings.write`. Without
 * write the list still reads; without read the tab says why it is empty.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banner, Btn, Checkbox, ConfirmDialog, DatePicker, Field, Icon, IconTile, InlineEmptyState,
  Input, RowActions, Section, StatusPill, Table, TimeSelect, T,
} from "@v0y4ge/jrni-ui";
import { admin } from "@v0y4ge/api-client";
import { LoadState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { useStaffAvailability, displayZoneName, zoneSource } from "./useStaffAvailability";
import { ZoneLoadingBanner, ZoneNote, StaffZoneBanner } from "./StaffAvailabilityTab";

const PANEL: React.CSSProperties = { width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 14 };

type AvailabilityOverrideRecord = admin.AvailabilityOverrideRecord;
const { listOverrides, createOverride, deleteOverride } = admin;

/** A staff wall-clock must map to exactly one UTC instant in the staff zone. */
function isResolvableIanaZone(tz: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(); return !!tz; } catch { return false; }
}
function wallParts(ms: number, tz: string): string {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(ms)).reduce<Record<string, string>>((a, x) => { if (x.type !== "literal") a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
export type WallClockResult =
  | { kind: "valid"; iso: string }
  | { kind: "nonexistent"; message: string }
  | { kind: "ambiguous"; message: string };
export function resolveWallClock(ymd: string, hm: string, tz: string): WallClockResult {
  if (!isResolvableIanaZone(tz)) return { kind: "nonexistent", message: "Choose a valid staff time zone before saving." };
  const [y, mo, d] = ymd.split("-").map(Number);
  const [h, mi] = hm.split(":").map(Number);
  const targetDate = new Date(Date.UTC(y, mo - 1, d, h === 24 ? 0 : h, mi));
  if (h === 24) targetDate.setUTCDate(targetDate.getUTCDate() + 1);
  const target = `${targetDate.getUTCFullYear()}-${String(targetDate.getUTCMonth() + 1).padStart(2, "0")}-${String(targetDate.getUTCDate()).padStart(2, "0")}T${String(targetDate.getUTCHours()).padStart(2, "0")}:${String(targetDate.getUTCMinutes()).padStart(2, "0")}`;
  const guess = targetDate.getTime();
  const offsets = new Set<number>();
  for (const delta of [-172800000, -86400000, -7200000, -3600000, 0, 3600000, 7200000, 86400000, 172800000]) {
    const instant = guess + delta;
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(instant)).reduce<Record<string, number>>((a, x) => { if (x.type !== "literal") a[x.type] = Number(x.value); return a; }, {});
    offsets.add(Math.round((Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second) - instant) / 60000));
  }
  const matches = [...offsets].map((offset) => guess - offset * 60000).filter((ms, i, all) => all.indexOf(ms) === i && wallParts(ms, tz) === target);
  if (matches.length === 1) return { kind: "valid", iso: new Date(matches[0]).toISOString() };
  if (matches.length > 1) return { kind: "ambiguous", message: "Choose a time that occurs once in the staff time zone; this wall time repeats during the clock change." };
  return { kind: "nonexistent", message: "Choose a time that exists in the staff time zone; this wall time is skipped during the clock change." };
}
export function wallToUtcIso(ymd: string, hm: string, tz: string): string {
  const result = resolveWallClock(ymd, hm, tz);
  if (result.kind !== "valid") throw new Error(result.message);
  return result.iso;
}
function addDay(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}
function fmt(iso: string, tz: string, withTime: boolean): string {
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "short", day: "numeric", month: "short", year: "numeric", ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}) }).format(new Date(iso));
}
/** Midnight-to-midnight in `tz` — shown as dates only. */
function isWholeDays(o: AvailabilityOverrideRecord, tz: string): boolean {
  const atMidnight = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)) === "00:00";
  return atMidnight(o.start_utc) && atMidnight(o.end_utc);
}

function useStaffAbsences(staffId: string | null, enabled: boolean) {
  const [rows, setRows] = useState<AvailabilityOverrideRecord[]>([]);
  const [loading, setLoading] = useState(!!staffId && enabled);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    if (!staffId || !enabled) { setRows([]); setLoading(false); setError(null); return; }
    setLoading(true); setError(null);
    listOverrides({ subject_kind: "staff", subject_id: staffId, override_kind: "closed" })
      .then((r) => {
        if (!live) return;
        const list = Array.isArray(r) ? r : [];
        // The query filters; this re-checks, so another person's row can never render here.
        setRows(list.filter((o) => o && o.subject_kind === "staff" && o.subject_id === staffId && o.override_kind === "closed"));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!live) return;
        console.warn("listAvailabilityOverrides failed:", e);
        setError("Couldn't load this person's absences.");
        setLoading(false);
      });
    return () => { live = false; };
  }, [staffId, enabled, nonce]);
  return { rows, loading, error, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

interface Draft { from: string; to: string; allDay: boolean; startTime: string; endTime: string; reason: string }

export function StaffAbsencesTab({ staffId, personName, ownTimezone, onOpenDetails }: {
  /** Opens the tab where the person's time zone is set (the zone banner's fix). */
  onOpenDetails?: () => void;
  staffId: string | null;
  personName: string;
  /** The person's OWN zone from the roster record — see StaffAvailabilityTab. */
  ownTimezone?: string | null;
}) {
  const t = useT();
  const canRead = can("settings.read");
  const canWrite = can("settings.write");
  const av = useStaffAvailability(staffId);
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const staffTz = av.timezone?.trim() || "";
  const staffZoneValid = isResolvableIanaZone(staffTz);
  const tz = staffZoneValid ? staffTz : viewerTz;
  const zoneSourceOf = zoneSource(av.timezone, ownTimezone, av.rules.length > 0);
  const abs = useStaffAbsences(staffId, canRead);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  /* 422 AVAILABILITY_OVERRIDE_INVALID names each bad field in fields[] (CC, 28 Sept):
     each message sits by its own field; one naming no field here stays in the banner. */
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
  const [showPast, setShowPast] = useState(false);
  const [confirm, setConfirm] = useState<AvailabilityOverrideRecord | null>(null);

  const now = Date.now();
  const sorted = useMemo(() => [...abs.rows].sort((a, b) => a.start_utc.localeCompare(b.start_utc)), [abs.rows]);
  const upcoming = sorted.filter((o) => Date.parse(o.end_utc) > now);
  const past = sorted.filter((o) => Date.parse(o.end_utc) <= now).reverse();
  const shown = showPast ? past : upcoming;

  const draftValidation = draft ? (() => {
    if (!staffZoneValid) return { span: null, reversed: false, fieldErr: { start_utc: "A valid staff time zone is required before saving.", end_utc: "A valid staff time zone is required before saving." } };
    if (!draft.from || !draft.to) return { span: null, reversed: false, fieldErr: {} };
    const start = resolveWallClock(draft.from, draft.allDay ? "00:00" : draft.startTime, staffTz);
    const end = resolveWallClock(draft.allDay ? addDay(draft.to) : draft.to, draft.allDay ? "00:00" : draft.endTime, staffTz);
    const fieldErr: Record<string, string> = {};
    if (start.kind !== "valid") fieldErr.start_utc = start.message;
    if (end.kind !== "valid") fieldErr.end_utc = end.message;
    /* A bound that does not exist (or repeats) in the staff zone has no instant,
       so there is no order to compare: its own message is the whole story, and
       "has to end after it starts" beside it would be a second, false reason. */
    if (start.kind !== "valid" || end.kind !== "valid") return { span: null, reversed: false, fieldErr };
    const reversed = !(Date.parse(end.iso) > Date.parse(start.iso));
    const span = reversed ? null : { start: start.iso, end: end.iso };
    return { span, reversed, fieldErr };
  })() : { span: null, reversed: false, fieldErr: {} };
  const span = draftValidation.span;

  const save = async () => {
    if (!staffId || !draft || busy) return;
    if (!staffZoneValid) { setSaveErr("A valid staff time zone is required before saving."); return; }
    if (!span) { setFieldErr({}); setSaveErr("Choose valid times for this staff member's time zone."); return; }
    setBusy(true); setSaveErr(""); setFieldErr({});
    try {
      await createOverride({ subject_kind: "staff", subject_id: staffId, override_kind: "closed", start_utc: span.start, end_utc: span.end, reason: draft.reason.trim() || null });
      setDraft(null);
      toast({ tone: "success", title: t("staffAbsences.added", "Absence added"), subtitle: personName });
      abs.reload();
    } catch (e) {
      const err = e as { code?: string; fields?: { path?: string; message?: string }[]; message?: string } | null;
      const byField: Record<string, string> = {};
      const other: string[] = [];
      if (err?.code === "AVAILABILITY_OVERRIDE_INVALID" && Array.isArray(err.fields)) {
        for (const f of err.fields) {
          const key = String(f?.path || "").split(".").pop() || "";
          const msg = String(f?.message || "");
          if (!msg) continue;
          if (key === "start_utc" || key === "end_utc" || key === "reason") byField[key] = byField[key] ? byField[key] + " " + msg : msg;
          else other.push(msg);
        }
      }
      setFieldErr(byField);
      setSaveErr(other.length ? other.join(" ") : Object.keys(byField).length ? "" : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };
  const updateDraft = (fn: (current: Draft) => Draft) => {
    setDraft((current) => current ? fn(current) : current);
    setFieldErr({}); setSaveErr("");
  };
  const fieldNote = (key: string) => (draftValidation.fieldErr[key] || fieldErr[key])
    ? <div role="alert" style={{ fontSize: 12, color: T.red1, marginTop: 4 }}>{draftValidation.fieldErr[key] || fieldErr[key]}</div>
    : null;

  const remove = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      await deleteOverride(confirm.id);
      toast({ tone: "success", title: t("staffAbsences.removed", "Absence removed"), subtitle: personName });
      setConfirm(null);
      abs.reload();
    } catch (e) {
      toast({ tone: "danger", title: t("staffAbsences.couldntRemove", "Couldn't remove this absence"), subtitle: e instanceof Error ? e.message : undefined });
    } finally { setBusy(false); }
  };

  if (!staffId) {
    return (
      <div style={PANEL}>
        <Section title={t("staffRoles.tabAbsences", "Absences")} description={t("staffRoles.absencesBlurb", "Holidays, time off and other absences for this person.")}>
          <InlineEmptyState style={{ marginTop: 8 }} icon={<IconTile icon={<Icon.clock size={16} />} />}
            title={t("staffAvailability.noRosterRecord", "No roster record")}
            description={t("staffAbsences.noRosterRecordBody", "Absences are held against the roster record, and this person has a workspace membership only. Add them to the staff roster first.")} />
        </Section>
      </div>
    );
  }
  if (!canRead) {
    return (
      <div style={PANEL}>
        <Section title={t("staffRoles.tabAbsences", "Absences")} description={t("staffRoles.absencesBlurb", "Holidays, time off and other absences for this person.")}>
          <InlineEmptyState style={{ marginTop: 8 }} icon={<IconTile icon={<Icon.lock size={16} />} />}
            title={t("staffAbsences.noAccess", "You can't view absences")}
            description={t("staffAbsences.noAccessBody", "Absences are availability settings. Viewing them needs the Settings read permission.")} />
        </Section>
      </div>
    );
  }

  const columns = [
    { id: "when", header: t("staffAbsences.when", "When"), minWidth: 260, cell: (o: AvailabilityOverrideRecord) => {
      const whole = isWholeDays(o, tz);
      const lastDay = whole ? new Date(Date.parse(o.end_utc) - 1).toISOString() : o.end_utc;
      const a = fmt(o.start_utc, tz, !whole), b = fmt(lastDay, tz, !whole);
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ color: T.n1, fontVariantNumeric: "tabular-nums" }}>{a === b ? a : a + " – " + b}</span>
          {!whole ? <StatusPill tone="neutral" size="sm">{t("staffAbsences.partDay", "Part day")}</StatusPill> : null}
        </div>
      );
    } },
    { id: "reason", header: t("staffAbsences.reason", "Reason"), minWidth: 180, cell: (o: AvailabilityOverrideRecord) => (
      <span style={{ color: o.reason ? T.n1 : T.soft }}>{o.reason || "—"}</span>
    ) },
    { id: "act", header: "", minWidth: 60, cell: (o: AvailabilityOverrideRecord) => canWrite ? (
      <RowActions size={28} moreItems={[{ label: t("common.actions.remove", "Remove"), icon: <Icon.x size={13} />, danger: true, onClick: () => setConfirm(o) }]} />
    ) : null },
  ];

  return (
    <div style={PANEL}>
      {/* The zone line answers only once the availability read has: before that
          the zone is unknown, not absent, so it is a skeleton (and the list
          below waits too — its dates are formatted in that zone). */}
      {!av.loaded && !av.error ? <ZoneLoadingBanner /> : !av.loaded ? (
        <Banner tone="danger" size="compact" testid="staff-zone-error"
          title={t("staffAbsences.tzLoadFailed", "Couldn't load this person's time zone")}
          action={<Btn size="sm" variant="secondary" onClick={av.reload}>{t("common.actions.retry", "Retry")}</Btn>}>
          <ZoneNote>{t("staffAbsences.tzLoadFailedBody", "Dates below are shown in yours — {zone}. Adding an absence is unavailable until it loads.", { zone: displayZoneName(viewerTz) })}</ZoneNote>
        </Banner>
      ) : (
        <StaffZoneBanner zone={av.timezone} source={zoneSourceOf} valid={staffZoneValid} onFix={onOpenDetails}
          consequence={t("staffZone.absencesConsequence", "Dates below are shown in your time zone ({zone}), and you can't add an absence until it's set.", { zone: displayZoneName(viewerTz) })} />
      )}


      {draft ? (
        <Section density="content" title={t("staffAbsences.addTitle", "Add an absence")}
          description={t("staffAbsences.addBody", "Customers can't book this person during it, and new bookings aren't assigned to them. Bookings they already have are not moved.")}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginTop: 14 }}>
            <div>
              <DatePicker label={t("staffAbsences.from", "From")} value={draft.from || null} clearable={false} timeZone={tz}
                onChange={(v) => updateDraft((d) => ({ ...d, from: String(v || "").slice(0, 10), to: d.to && d.to >= String(v || "").slice(0, 10) ? d.to : String(v || "").slice(0, 10) }))} full />
              {draft.allDay ? fieldNote("start_utc") : null}
            </div>
            <div>
              <DatePicker label={t("staffAbsences.to", "To")} value={draft.to || null} clearable={false} timeZone={tz}
                onChange={(v) => updateDraft((d) => ({ ...d, to: String(v || "").slice(0, 10) }))} full />
              {draft.allDay ? fieldNote("end_utc") : null}
            </div>
            {!draft.allDay ? (
              <>
                <div>
                  <TimeSelect label={t("staffAbsences.startsAt", "Starts at")} value={draft.startTime} allowEmpty={false} onChange={(v) => updateDraft((d) => ({ ...d, startTime: v }))} />
                  {fieldNote("start_utc")}
                </div>
                <div>
                  <TimeSelect label={t("staffAbsences.endsAt", "Ends at")} value={draft.endTime} allowEmpty={false} onChange={(v) => updateDraft((d) => ({ ...d, endTime: v }))} />
                  {fieldNote("end_utc")}
                </div>
              </>
            ) : null}
          </div>
          <div style={{ marginTop: 12 }}>
            <Checkbox checked={draft.allDay} onChange={(v: boolean) => updateDraft((d) => ({ ...d, allDay: !!v }))} label={t("staffAbsences.allDay", "Whole days")} />
          </div>
          <div style={{ marginTop: 12 }}>
            <Field label={t("staffAbsences.reasonOptional", "Reason (optional)")}>
              <Input value={draft.reason} maxLength={500} onChange={(e) => updateDraft((d) => ({ ...d, reason: e.target.value }))} placeholder={t("staffAbsences.reasonPlaceholder", "e.g. Annual leave")} />
            </Field>
            {fieldNote("reason")}
          </div>
          {draftValidation.reversed ? (
            <Banner tone="danger" size="compact" style={{ marginTop: 12 }} title={t("staffAbsences.endAfterStart", "An absence has to end after it starts.")} />
          ) : null}
          {saveErr ? <Banner tone="danger" size="compact" style={{ marginTop: 12 }} title={t("staffAbsences.couldntSave", "Couldn't save this absence")}>{saveErr}</Banner> : null}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
            <Btn size="sm" variant="secondary" disabled={busy} onClick={() => { setDraft(null); setSaveErr(""); setFieldErr({}); }}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn size="sm" disabled={busy || !span || !staffZoneValid} onClick={() => void save()}>{busy ? t("staffAvailability.saving", "Saving…") : t("common.actions.save", "Save")}</Btn>
          </div>
        </Section>
      ) : null}

      <Section density="dense"
        title={t("staffRoles.tabAbsences", "Absences")}
        description={t("staffAbsences.listBody", "Times this person can't be booked, whether an admin added them here or they blocked the time on My Day.")}
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn size="sm" variant="secondary" onClick={() => setShowPast((v) => !v)}>
              {showPast ? t("staffAbsences.showUpcoming", "Show upcoming") : t("staffAbsences.showPast", "Show past")}
            </Btn>
            <Btn size="sm" icon={<Icon.plus size={12} />} disabled={!canWrite || busy || !!draft || !av.loaded || !staffZoneValid}
              title={!canWrite ? t("staffAbsences.needsWrite", "Adding absences needs the Settings write permission.") : !av.loaded ? undefined : !staffZoneValid ? "A valid staff time zone is required before saving." : undefined}
              onClick={() => { const today = new Date().toLocaleDateString("en-CA", { timeZone: tz }); setDraft({ from: today, to: today, allDay: true, startTime: "09:00", endTime: "17:00", reason: "" }); }}>
              {t("staffAvailability.add", "Add")}
            </Btn>
          </div>
        }>
        <div style={{ marginTop: 12 }}>
          <LoadState loading={abs.loading || (!av.loaded && !av.error)} error={abs.error} onRetry={abs.reload} skeleton="table"
            loadingLabel={t("staffAbsences.loadingList", "Loading this person's absences…")}>
            {!shown.length ? (
              <InlineEmptyState icon={<IconTile icon={<Icon.clock size={16} />} />}
                title={showPast ? t("staffAbsences.noPast", "No past absences") : t("staffAbsences.noUpcoming", "No upcoming absences")}
                description={showPast ? t("staffAbsences.noPastBody", "Nothing has blocked this person's time before today.") : t("staffAbsences.noUpcomingBody", "This person can be booked across their whole weekly pattern.")} />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table columns={columns} rows={shown} getRowId={(o) => o.id} />
              </div>
            )}
          </LoadState>
        </div>
      </Section>

      <ConfirmDialog open={!!confirm} tone="danger" busy={busy}
        title={t("staffAbsences.removeTitle", "Remove this absence?")}
        body={confirm ? <b>{(() => { const whole = isWholeDays(confirm, tz); return fmt(confirm.start_utc, tz, !whole); })()}</b> : undefined}
        consequence={t("staffAbsences.removeConsequence", "This time becomes bookable again straight away.")}
        confirmLabel={busy ? t("staffAbsences.removing", "Removing…") : t("common.actions.remove", "Remove")}
        cancelLabel={t("common.actions.cancel", "Cancel")}
        onCancel={() => { if (!busy) setConfirm(null); }}
        onConfirm={() => { void remove(); }}
        testid="staff-absence-remove-confirm" />
    </div>
  );
}
